import { useState, useEffect, useCallback, useRef } from 'react';
import alertService from '../services/alertService';


export default function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const pollRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await alertService?.getNotifications({ limit: 50 });
      // API returns { data: { notifications: [...], unread_count: N } }
      // Guard: always ensure we set an array, never an object
      const body = res?.data?.data;
      const list = Array.isArray(body)
        ? body
        : Array.isArray(body?.notifications)
        ? body.notifications
        : null;
      if (list !== null) setNotifications(list);
    } catch {
      // Keep current data on error (no crash, no fake reset)
    }
  }, []);

  useEffect(() => {
    // Fetch immediately on mount so we don't show fake data for 60 seconds
    fetchNotifications();
    // Then poll every 60 seconds
    pollRef.current = setInterval(fetchNotifications, 60000);
    return () => clearInterval(pollRef?.current);
  }, [fetchNotifications]);

  const unreadCount = notifications?.filter((n) => !n?.is_read && !n?.is_dismissed)?.length || 0;

  const markRead = useCallback(async (id) => {
    setNotifications((prev) =>
      prev?.map((n) => (n?.id === id ? { ...n, is_read: 1 } : n))
    );
    try { await alertService?.markRead(id); } catch { /* optimistic */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev?.map((n) => ({ ...n, is_read: 1 })));
    try { await alertService?.markAllRead(); } catch { /* optimistic */ }
  }, []);

  const dismiss = useCallback(async (id) => {
    setNotifications((prev) =>
      prev?.map((n) => (n?.id === id ? { ...n, is_dismissed: 1 } : n))
    );
    try { await alertService?.dismissNotification(id); } catch { /* optimistic */ }
  }, []);

  const dismissAll = useCallback(async () => {
    setNotifications((prev) =>
      prev?.map((n) => (n?.is_read ? { ...n, is_dismissed: 1 } : n))
    );
    try { await alertService?.dismissAll(); } catch { /* optimistic */ }
  }, []);

  const refetch = useCallback(() => fetchNotifications(), [fetchNotifications]);

  return { notifications, unreadCount, isLoading, markRead, markAllRead, dismiss, dismissAll, refetch };
}
